import { useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { Switch } from '@/common/ui/switch'
import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import showErrorToast from '@/common/utils/showErrorToast'
import useQueryGetTelegramNotifySetting from '../../api/hooks/useQueryGetTelegramNotifySetting'
import useMutationUpdateTelegramNotifySetting from '../../api/hooks/useMutationUpdateTelegramNotifySetting'
import { TelegramNotifyQueryKeys } from '../../keys'
import TelegramNotifyForm from '../../components/TelegramNotifyForm'

import type { UpdateTelegramNotifySettingDto } from '../../types'

type SettingKey = keyof Pick<
	UpdateTelegramNotifySettingDto,
	| 'enableAnalyticsCron'
	| 'enableWithdrawApproval'
	| 'enableDepositComplete'
	| 'enableWithdrawComplete'
	| 'enableDepositFailed'
	| 'enableWithdrawFailed'
	| 'enableBigWin'
	| 'enableTrueMoneyNoMatch'
	| 'enableCryptoInvoice'
	| 'enableTransactionExpired'
>

const SETTING_LABELS: Record<
	SettingKey,
	{ title: string; description: string }
> = {
	enableAnalyticsCron: {
		title: 'แจ้งเตือนสถิติรายวัน',
		description: 'เปิดหรือปิดการแจ้งเตือนสถิติรายวัน',
	},
	enableWithdrawApproval: {
		title: 'แจ้งเตือนอนุมัติถอนเงิน',
		description: 'เปิดหรือปิดการแจ้งเตือนเมื่อมีรายการถอนเงินรออนุมัติ',
	},
	enableDepositComplete: {
		title: 'แจ้งเตือนฝากเงินสำเร็จ',
		description: 'เปิดหรือปิดการแจ้งเตือนเมื่อฝากเงินสำเร็จ',
	},
	enableWithdrawComplete: {
		title: 'แจ้งเตือนถอนเงินสำเร็จ',
		description: 'เปิดหรือปิดการแจ้งเตือนเมื่อถอนเงินสำเร็จ',
	},
	enableDepositFailed: {
		title: 'แจ้งเตือนฝากเงินล้มเหลว',
		description: 'เปิดหรือปิดการแจ้งเตือนเมื่อฝากเงินล้มเหลว',
	},
	enableWithdrawFailed: {
		title: 'แจ้งเตือนถอนเงินล้มเหลว',
		description: 'เปิดหรือปิดการแจ้งเตือนเมื่อถอนเงินล้มเหลว',
	},
	enableBigWin: {
		title: 'แจ้งเตือนชนะรางวัลใหญ่',
		description: 'เปิดหรือปิดการแจ้งเตือนเมื่อผู้เล่นชนะรางวัลใหญ่',
	},
	enableTrueMoneyNoMatch: {
		title: 'แจ้งเตือน TrueMoney ไม่ตรงกัน',
		description: 'เปิดหรือปิดการแจ้งเตือนเมื่อ TrueMoney ไม่ตรงกัน',
	},
	enableCryptoInvoice: {
		title: 'แจ้งเตือน Crypto Invoice',
		description: 'เปิดหรือปิดการแจ้งเตือน Crypto Invoice',
	},
	enableTransactionExpired: {
		title: 'แจ้งเตือนรายการหมดอายุ',
		description: 'เปิดหรือปิดการแจ้งเตือนเมื่อรายการหมดอายุ',
	},
}

const SETTING_KEYS: SettingKey[] = [
	'enableAnalyticsCron',
	'enableWithdrawApproval',
	'enableDepositComplete',
	'enableWithdrawComplete',
	'enableDepositFailed',
	'enableWithdrawFailed',
	'enableBigWin',
	'enableTrueMoneyNoMatch',
	'enableCryptoInvoice',
	'enableTransactionExpired',
]

export default function TelegramNotifyPage() {
	const queryClient = useQueryClient()
	const [loadingKey, setLoadingKey] = useState<SettingKey | null>(null)

	const {
		data,
		isLoading: isQueryLoading,
		error,
	} = useQueryGetTelegramNotifySetting()
	const { mutateAsync: updateTelegramNotifySetting } =
		useMutationUpdateTelegramNotifySetting()

	const handleSwitchChange = async (key: SettingKey, value: boolean) => {
		setLoadingKey(key)
		try {
			await updateTelegramNotifySetting({
				[key]: value,
			})
			toast.success('อัพเดทการตั้งค่าการแจ้งเตือน Telegram สำเร็จ')
			queryClient.invalidateQueries({
				queryKey: [TelegramNotifyQueryKeys.GetTelegramNotifySetting],
			})
		} catch (err) {
			showErrorToast(err as Error)
		} finally {
			setLoadingKey(null)
		}
	}

	if (isQueryLoading) {
		return (
			<div className="flex items-center justify-center p-8">
				<div className="text-muted-foreground">กำลังโหลด...</div>
			</div>
		)
	}

	if (error) {
		return (
			<div className="flex items-center justify-center p-8">
				<div className="text-destructive">ไม่สามารถโหลดข้อมูลได้</div>
			</div>
		)
	}

	return (
		<div className="container mx-auto space-y-6 p-6">
			<h1 className="text-2xl font-bold">การตั้งค่าแจ้งเตือน Telegram</h1>

			{data?.isEnabled ? (
				<>
					<Card>
						<CardHeader>
							<CardTitle>ตั้งค่าช่องทางการแจ้งเตือน</CardTitle>
						</CardHeader>
						<CardContent>
							<TelegramNotifyForm data={data} />
						</CardContent>
					</Card>
					<Card>
						<CardHeader>
							<CardTitle>การแจ้งเตือน</CardTitle>
						</CardHeader>
						<CardContent className="space-y-6">
							{SETTING_KEYS.map((key) => (
								<div key={key} className="flex items-center justify-between">
									<div className="space-y-0.5">
										<label className="text-sm font-medium">
											{SETTING_LABELS[key].title}
										</label>
										<p className="text-sm text-muted-foreground">
											{SETTING_LABELS[key].description}
										</p>
									</div>
									<Switch
										checked={data?.[key] ?? false}
										onCheckedChange={(value) => handleSwitchChange(key, value)}
										disabled={loadingKey !== null}
									/>
								</div>
							))}
						</CardContent>
					</Card>
				</>
			) : (
				<Card>
					<CardContent>
						<div className="flex items-center justify-center p-16">
							<div className="text-muted-foreground">
								การแจ้งเตือน telegram ของคุณปิดอยู่กรุณาแจ้ง API support
							</div>
						</div>
					</CardContent>
				</Card>
			)}
		</div>
	)
}
